import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/utils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Plus, Pencil, Trash2, GraduationCap, Loader2, Lock } from "lucide-react";

/**
 * Qualification Levels — global master catalog.
 *
 * Admins define their own award levels here (Competency Certificate,
 * Certificate II, NOSS Level 3, Diploma, etc.). These levels can then be
 * attached to frameworks (SWEP, NOSS, Ofqual...) and competencies are
 * organised under each Framework + Level pair.
 *
 * Legacy levels (diploma / bachelor / master / doctoral) are seeded by the
 * backend on first read so this catalog is never empty.
 */
const EMPTY_FORM = { name: "", code: "", description: "", numeric_rank: "" };

export default function QualificationLevelsPanel({ onChanged }) {
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const fetchLevels = useCallback(async () => {
    try {
      const r = await api.get("/qualification-levels");
      setLevels(r.data || []);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load qualification levels.");
    } finally {
      setLoading(false);
    }
  }, [onChanged]);

  useEffect(() => { fetchLevels(); }, [fetchLevels]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (level) => {
    setEditing(level);
    setForm({
      name: level.name || "",
      code: level.code || "",
      description: level.description || "",
      numeric_rank: level.numeric_rank ?? "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) {
      toast.error("Level name is required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        numeric_rank: form.numeric_rank === "" ? null : Number(form.numeric_rank),
      };
      if (!editing) payload.code = form.code.trim();
      if (editing) {
        await api.put(`/qualification-levels/${editing.level_id}`, payload);
        toast.success("Qualification level updated.");
      } else {
        await api.post("/qualification-levels", payload);
        toast.success("Qualification level created.");
      }
      setDialogOpen(false);
      fetchLevels();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/qualification-levels/${pendingDelete.level_id}`);
      toast.success("Qualification level archived.");
      setPendingDelete(null);
      fetchLevels();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <Card data-testid="qualification-levels-panel">
      <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
        <div>
          <CardTitle className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-blue-600" />
            Qualification Levels
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Master catalog of award levels (Competency Certificate, Certificate II, NOSS Level 3, Diploma, etc.).
            Attach these to frameworks and use them to organise competencies.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="add-qualification-level-btn">
          <Plus className="w-4 h-4 mr-2" /> New Level
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading qualification levels…
          </div>
        ) : levels.length === 0 ? (
          <p className="text-sm text-slate-500 py-6">No qualification levels yet. Click <em>New Level</em> to add one.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="qualification-levels-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Code</th>
                  <th className="py-2 pr-3">Rank</th>
                  <th className="py-2 pr-3">Description</th>
                  <th className="py-2 pr-3 w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {levels.map((lv) => (
                  <tr key={lv.level_id} className="border-b border-slate-100" data-testid={`level-row-${lv.code}`}>
                    <td className="py-2 pr-3 font-medium text-slate-900">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{lv.name}</span>
                        {lv.is_legacy ? (
                          <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600" title="Seeded automatically — code cannot change">
                            <Lock className="w-2.5 h-2.5 mr-1" /> Legacy
                          </Badge>
                        ) : null}
                      </div>
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs text-slate-700">{lv.code}</td>
                    <td className="py-2 pr-3 text-slate-700">{lv.numeric_rank ?? "—"}</td>
                    <td className="py-2 pr-3 text-slate-600">{lv.description || "—"}</td>
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(lv)} data-testid={`edit-level-${lv.code}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-rose-600"
                          onClick={() => setPendingDelete(lv)}
                          disabled={lv.is_legacy}
                          title={lv.is_legacy ? "Legacy levels can't be deleted (they may be referenced by historical candidates)." : "Archive this level"}
                          data-testid={`delete-level-${lv.code}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="qualification-level-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Qualification Level" : "New Qualification Level"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 mt-2">
            <div className="space-y-1.5">
              <Label htmlFor="ql-name">Name *</Label>
              <Input
                id="ql-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Certificate II, Competency Certificate, NOSS Level 3"
                data-testid="qualification-level-name-input"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ql-code">Code {editing ? <span className="text-slate-400 text-xs">(immutable)</span> : <span className="text-slate-400 text-xs">(auto-derived from name if blank)</span>}</Label>
              <Input
                id="ql-code"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="e.g. cert_2, noss_l3"
                disabled={Boolean(editing)}
                data-testid="qualification-level-code-input"
              />
              <p className="text-[11px] text-slate-500">Short stable identifier used by frameworks and competencies. Cannot be changed once saved.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ql-rank">Numeric Rank <span className="text-slate-400 text-xs">(optional ordering — 1 = lowest)</span></Label>
              <Input
                id="ql-rank"
                type="number"
                value={form.numeric_rank}
                onChange={(e) => setForm({ ...form, numeric_rank: e.target.value })}
                placeholder="e.g. 2 for Cert II, 4 for Diploma, 7 for Masters"
                data-testid="qualification-level-rank-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ql-desc">Description <span className="text-slate-400 text-xs">(optional)</span></Label>
              <Textarea
                id="ql-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What competency depth does this level represent?"
                rows={3}
                data-testid="qualification-level-desc-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="qualification-level-cancel-btn">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="qualification-level-save-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editing ? "Save changes" : "Create level"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(pendingDelete)} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
        <AlertDialogContent data-testid="qualification-level-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this qualification level?</AlertDialogTitle>
            <AlertDialogDescription>
              The level <strong>{pendingDelete?.name}</strong> will be marked inactive. Existing frameworks and
              competencies that already reference it will keep working — but it will no longer appear in pickers
              for new records. You can re-activate it later from the API.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="qualification-level-confirm-delete-btn" className="bg-rose-600 hover:bg-rose-700">
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
